// Per-chunk Three.js mesh manager (client side of ENG-12/15/21).
//
// Streaming meshes are computed OFF the main thread by the mesh worker and
// uploaded under a per-frame budget, so teleporting/flying never causes a long
// frame. Edits remesh SYNCHRONOUSLY on the main thread for instant feedback.
// Each chunk has a `rev`; an edit bumps it, so an in-flight worker result made
// stale by that edit is dropped on arrival (E-3). A sync fallback runs when no
// worker is present (Node-ish/failure).
import * as THREE from 'three';
import { STREAM, VIEW, WORLD } from '../../sim/config';
import { meshChunk, type GeoBuffers } from '../../sim/mesh/mesher';
import type { RenderTable } from '../../sim/mesh/atlasmap';
import {
  chunkToPayload,
  meshRequestTransferables,
  type MeshOutbound,
  type MeshRequestMsg,
} from '../../sim/workers/protocol';
import type { World } from '../../sim/engine/world';
import { WorkerPool, poolSize, type WorkerLike } from '../streaming/pool';
import { focusPoint, focusDist2, scaledBudget, speedBlocksPerTick, type Heading } from '../streaming/priority';

const REQUEST_BUDGET = STREAM.MESH_REQUEST_BASE; // baseline mesh jobs dispatched per frame
const EVICT_BUDGET = 24; // mesh disposals per frame — a teleport must not dispose
//                          hundreds of meshes in one frame (dispose-storm stall).
const key = (cx: number, cz: number): string => `${cx},${cz}`;

/** Build the real mesh-worker pool (browser only; sized to the machine). Flying an
 *  aircraft outruns a single mesher, so meshing fans across cores (perf). */
export function makeMeshWorkers(): Worker[] {
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
  const n = poolSize(cores, STREAM.MESH_WORKERS_MAX);
  const out: Worker[] = [];
  for (let i = 0; i < n; i++) {
    out.push(new Worker(new URL('../../sim/workers/mesh.worker.ts', import.meta.url), { type: 'module' }));
  }
  return out;
}

interface ChunkMeshEntry {
  opaque: THREE.Mesh | null;
  cutout: THREE.Mesh | null;
  translucent: THREE.Mesh | null;
}

interface MeshBuffers {
  originX: number;
  originZ: number;
  opaque: GeoBuffers | null;
  cutout: GeoBuffers | null;
  translucent: GeoBuffers | null;
}

function toGeometry(buf: GeoBuffers): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(buf.positions, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(buf.normals, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(buf.uvs, 2));
  g.setAttribute('color', new THREE.BufferAttribute(buf.colors, 3));
  // Per-vertex light levels (0..1); the shader mixes sky by the daylight uniform.
  g.setAttribute('aSky', new THREE.BufferAttribute(buf.sky, 1));
  g.setAttribute('aBlock', new THREE.BufferAttribute(buf.block, 1));
  g.setIndex(new THREE.BufferAttribute(buf.indices, 1));
  g.computeBoundingSphere();
  return g;
}

export class ChunkMeshes {
  private readonly entries = new Map<string, ChunkMeshEntry>();
  private readonly rev = new Map<string, number>();
  private readonly requested = new Set<string>();
  /** LOD depth each loaded mesh was built at (0 = full). A chunk whose built LOD
   *  differs from the current viewLod is re-meshed (perf: surface-only at speed). */
  private readonly builtLod = new Map<string, number>();
  private queue: MeshOutbound[] = [];
  private jobId = 1;
  private viewLod = 0;
  private readonly pool: WorkerPool<MeshOutbound> | null;
  /** Increments on every geometry upload — instrumentation for the edit test. */
  rebuildCount = 0;

  constructor(
    private readonly world: World,
    private readonly table: RenderTable,
    private readonly scene: THREE.Scene,
    private readonly material: THREE.Material,
    private readonly waterMaterial: THREE.Material,
    workers?: readonly WorkerLike[],
  ) {
    // A pool of ≥1 worker meshes off-thread; no workers ⇒ synchronous fallback
    // (Node-ish / the replay view, which remeshes the whole world each tick).
    this.pool = workers && workers.length ? new WorkerPool<MeshOutbound>(workers, (d) => this.onResult(d)) : null;
    this.pool?.broadcast({ type: 'meshInit', table });
  }

  /** How many worker threads back the mesher (0 ⇒ synchronous). */
  get workerCount(): number { return this.pool?.size ?? 0; }

  get loadedCount(): number {
    return this.entries.size;
  }

  /** Remove every chunk mesh from the scene and shut the worker pool down
   *  (dimension switch, DIM-1). */
  dispose(): void {
    for (const k of [...this.entries.keys()]) this.disposeEntry(k);
    this.requested.clear();
    this.queue = [];
    this.pool?.terminate();
  }

  private disposeEntry(k: string): void {
    const e = this.entries.get(k);
    if (!e) return;
    for (const mesh of [e.opaque, e.cutout, e.translucent]) {
      if (mesh) {
        this.scene.remove(mesh);
        mesh.geometry.dispose();
      }
    }
    this.entries.delete(k);
    this.builtLod.delete(k);
  }

  private upload(cx: number, cz: number, m: MeshBuffers, lod: number): void {
    this.disposeEntry(key(cx, cz));
    const entry: ChunkMeshEntry = { opaque: null, cutout: null, translucent: null };
    const layers = [
      ['opaque', m.opaque, this.material],
      ['cutout', m.cutout, this.material],
      ['translucent', m.translucent, this.waterMaterial],
    ] as const;
    for (const [layer, buf, mat] of layers) {
      if (!buf) continue;
      const mesh = new THREE.Mesh(toGeometry(buf), mat);
      mesh.position.set(m.originX, 0, m.originZ);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      entry[layer] = mesh;
      this.scene.add(mesh);
    }
    this.entries.set(key(cx, cz), entry);
    this.builtLod.set(key(cx, cz), lod);
    this.rebuildCount++;
  }

  private curRev(cx: number, cz: number): number {
    return this.rev.get(key(cx, cz)) ?? 0;
  }

  /** Mesh a chunk synchronously on the main thread (edits + fallback), honoring
   *  the current view LOD so an edit while flying stays consistent. */
  private syncBuild(cx: number, cz: number): void {
    const chunk = this.world.getChunk(cx, cz)!;
    const opts = this.viewLod > 0 ? { lodFloorDepth: this.viewLod } : undefined;
    this.upload(cx, cz, meshChunk(this.world, chunk, this.table, opts), this.viewLod);
  }

  /** Dispatch an off-thread mesh job — but ONLY when the chunk and its 4
   *  neighbors are already generated (by the gen worker). We never generate on
   *  the main thread here: doing so turned a teleport into a multi-second stall.
   *  If neighbors aren't ready yet, skip; a later frame retries once they arrive.
   *  With no worker (Node/failure), fall back to a synchronous build. */
  private requestBuild(cx: number, cz: number): void {
    if (!this.pool) {
      this.syncBuild(cx, cz);
      return;
    }
    const k = key(cx, cz);
    if (this.requested.has(k)) return;
    const self = this.world.getChunk(cx, cz, false);
    const px = this.world.getChunk(cx + 1, cz, false);
    const nx = this.world.getChunk(cx - 1, cz, false);
    const pz = this.world.getChunk(cx, cz + 1, false);
    const nz = this.world.getChunk(cx, cz - 1, false);
    if (!self || !px || !nx || !pz || !nz) return; // wait for the gen worker
    this.requested.add(k);
    const neighbors = [chunkToPayload(px), chunkToPayload(nx), chunkToPayload(pz), chunkToPayload(nz)];
    const msg: MeshRequestMsg = {
      type: 'mesh',
      jobId: this.jobId++,
      rev: this.curRev(cx, cz),
      cx,
      cz,
      self: chunkToPayload(self),
      neighbors,
      lod: this.viewLod,
    };
    this.pool.post(msg, meshRequestTransferables(msg));
  }

  private onResult(msg: MeshOutbound): void {
    this.requested.delete(key(msg.cx, msg.cz));
    if (msg.rev !== this.curRev(msg.cx, msg.cz)) return; // stale — edited since (E-3)
    this.queue.push(msg);
  }

  /** Rebuild the chunk owning (x,z) plus any border neighbor, after an edit.
   *  Synchronous for instant feedback; bumps rev to invalidate in-flight jobs. */
  markDirty(x: number, z: number): void {
    const cx = Math.floor(x / WORLD.SECTION), cz = Math.floor(z / WORLD.SECTION);
    const lx = x - cx * WORLD.SECTION, lz = z - cz * WORLD.SECTION;
    const dirty: Array<[number, number]> = [[cx, cz]];
    if (lx === 0) dirty.push([cx - 1, cz]);
    if (lx === WORLD.SECTION - 1) dirty.push([cx + 1, cz]);
    if (lz === 0) dirty.push([cx, cz - 1]);
    if (lz === WORLD.SECTION - 1) dirty.push([cx, cz + 1]);
    for (const [dx, dz] of dirty) {
      this.rev.set(key(dx, dz), this.curRev(dx, dz) + 1);
      this.syncBuild(dx, dz);
    }
  }

  /** Stream meshes around the player and evict far ones (budgeted). `heading`
   *  (blocks/tick) biases which missing chunks mesh first and scales the per-frame
   *  dispatch budget, so a fast flight meshes the terrain ahead of it first. */
  update(px: number, pz: number, renderDist: number = VIEW.RENDER_DIST_DEFAULT, heading: Heading = { vx: 0, vz: 0 }, lod = 0): void {
    const pcx = Math.floor(px / WORLD.SECTION), pcz = Math.floor(pz / WORLD.SECTION);
    const R = renderDist;
    const keep = R + VIEW.EVICT_MARGIN;
    this.viewLod = lod; // meshes built this frame (and re-meshes below) use this LOD
    const { fx, fz } = focusPoint(pcx, pcz, heading.vx, heading.vz, R);
    const speed = speedBlocksPerTick(heading);

    // 1. Upload finished meshes (budgeted, in-range, non-stale). The upload budget
    //    scales with speed too — a fast flight meshes plenty across the pool, so the
    //    main-thread upload step is the real throttle if it stays fixed.
    const uploadBudget = scaledBudget(STREAM.UPLOAD_BASE, speed, STREAM.UPLOAD_MAX);
    let uploaded = 0;
    const leftover: MeshOutbound[] = [];
    for (const msg of this.queue) {
      if (uploaded >= uploadBudget) { leftover.push(msg); continue; }
      if (msg.rev !== this.curRev(msg.cx, msg.cz)) continue; // became stale while queued
      if (Math.abs(msg.cx - pcx) > keep || Math.abs(msg.cz - pcz) > keep) continue; // drifted away
      this.upload(msg.cx, msg.cz, msg, msg.lod);
      uploaded++;
    }
    this.queue = leftover;

    // 2. Dispatch mesh jobs for the missing in-range chunks. The disc membership
    //    stays circular around the player; only the *order* follows the heading —
    //    chunks nearest the forward focus mesh first (perf).
    const wanted: Array<{ cx: number; cz: number; d2: number }> = [];
    for (let dx = -R; dx <= R; dx++) {
      for (let dz = -R; dz <= R; dz++) {
        if (dx * dx + dz * dz > R * R + 1) continue;
        const cx = pcx + dx, cz = pcz + dz;
        const k = key(cx, cz);
        if (this.requested.has(k)) continue;
        // Already meshed at the right LOD → done; a stale LOD needs a re-mesh.
        if (this.entries.has(k) && (this.builtLod.get(k) ?? 0) === lod) continue;
        wanted.push({ cx, cz, d2: focusDist2(cx, cz, fx, fz) });
      }
    }
    wanted.sort((a, b) => a.d2 - b.d2);
    // Keep every worker fed, and dispatch more per frame the faster we're moving.
    const reqBudget = scaledBudget(Math.max(REQUEST_BUDGET, this.pool?.size ?? 0), speed);
    for (let i = 0; i < Math.min(reqBudget, wanted.length); i++) {
      this.requestBuild(wanted[i]!.cx, wanted[i]!.cz);
    }

    // 3. Evict far meshes (budgeted — a teleport must not dispose hundreds at
    //    once) and forget far in-flight requests.
    let evicted = 0;
    for (const k of this.entries.keys()) {
      if (evicted >= EVICT_BUDGET) break;
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      if (Math.abs(cx - pcx) > keep || Math.abs(cz - pcz) > keep) {
        this.disposeEntry(k);
        evicted++;
      }
    }
    for (const k of this.requested) {
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      if (Math.abs(cx - pcx) > keep + 1 || Math.abs(cz - pcz) > keep + 1) this.requested.delete(k);
    }
  }

  /** Build the immediate 3×3 around the player synchronously so the first frame
   *  has solid ground before the worker warms up. */
  primeAround(px: number, pz: number): void {
    const pcx = Math.floor(px / WORLD.SECTION), pcz = Math.floor(pz / WORLD.SECTION);
    for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) this.syncBuild(pcx + dx, pcz + dz);
  }

  /** Remesh every currently-loaded chunk synchronously (M33 replay re-render: the
   *  whole world evolves under us each replay tick, so cached meshes are stale).
   *  Bumps each rev so any in-flight worker result is dropped on arrival (E-3). */
  remeshLoaded(): void {
    for (const k of [...this.entries.keys()]) {
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      this.rev.set(k, this.curRev(cx, cz) + 1);
      this.syncBuild(cx, cz);
    }
  }
}
